import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../services/api.client'

export interface ProjectElementRecord {
	elementId: string
	projectId: string
	elementType: string
	name: string
	description?: string
	environment?: string
	url?: string
	isActive: boolean
	createdAt: string
	updatedAt: string
	deletedAt: string | null
}

export interface ProjectElementFormData {
	elementType: string
	name: string
	description?: string
	environment?: string
	url?: string
	isActive?: boolean
}

export const useProjectElements = (projectId: string | null) =>
	useQuery({
		queryKey: ['project-elements', projectId],
		queryFn: async () => {
			if (!projectId) return []
			const response = await apiClient.get<ProjectElementRecord[]>(`/projects/${projectId}/elements`)
			return response.data || []
		},
		enabled: !!projectId,
	})

export const useCreateProjectElement = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async ({ projectId, data }: { projectId: string; data: ProjectElementFormData }) => {
			const response = await apiClient.post<ProjectElementRecord>(`/projects/${projectId}/elements`, data)
			return response.data
		},
		onSuccess: (_data, variables) => void qc.invalidateQueries({ queryKey: ['project-elements', variables.projectId] }),
	})
}

export const useUpdateProjectElement = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async ({
			projectId,
			elementId,
			data,
		}: {
			projectId: string
			elementId: string
			data: Partial<ProjectElementFormData>
		}) => {
			const response = await apiClient.put<ProjectElementRecord>(`/projects/${projectId}/elements/${elementId}`, data)
			return response.data
		},
		onSuccess: (_data, variables) => void qc.invalidateQueries({ queryKey: ['project-elements', variables.projectId] }),
	})
}

export const useDeleteProjectElement = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async ({ projectId, elementId }: { projectId: string; elementId: string }) => {
			await apiClient.delete(`/projects/${projectId}/elements/${elementId}`)
		},
		onSuccess: (_data, variables) => void qc.invalidateQueries({ queryKey: ['project-elements', variables.projectId] }),
	})
}
